// ─── Sous-module « inv-io » — exporter / importer / imprimer ──────
// Un seul endroit pour tout ce qui SORT de l'inventaire ou y ENTRE :
//   • JSON « modèle »  : structure seule (catégories + propriétés), réutilisable
//   • JSON « complet » : structure + objets (+ photos embarquées en base64)
//   • PDF récapitulatif ou feuille de comptage (avec ou sans photos, plans)
//   • planche de QR d'emplacements, à découper et coller sur les meubles
//   • import d'un fichier .json produit par MoPCal ou My Inventory
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useUI, useT, useEspace, Feuille, uploadFichier } from '@mop/core';
import {
  construireExportModele, construireExportComplet, preparerImportComplet,
  embarquerPhotosBase64, materialiserPhotos, nomFichierSafe, exporterFichier,
  lireFichierInventaire,
} from './services/io';
import { exporterInventairePdf } from './services/pdf';
import { imprimerPlancheQr } from './services/qr';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function FeuilleTransfert({
  visible, onFermer, inv, items, emplacements, onImporter, dossierPhotos,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { espaceActif } = useEspace();

  const [busy, setBusy] = useState('');
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');

  // Options d'export, mémorisées le temps de la feuille.
  const [avecPhotos, setAvecPhotos]   = useState(false);
  const [pdfPhotos, setPdfPhotos]     = useState(false);
  const [pdfCatsVides, setPdfCats]    = useState(false);
  const [pdfProps, setPdfProps]       = useState(true);
  const [pdfPlans, setPdfPlans]       = useState(true);

  const faire = async (cle, libelle, fn) => {
    if (busy) return;
    setBusy(cle); setErr(''); setMsg('');
    try { await fn(); setMsg(t('inventaire.transfTermine', { libelle })); }
    catch (e) { setErr(e?.message || String(e)); }
    setBusy('');
  };

  const exportModele = () => faire('modele', t('inventaire.transfExportModele'), async () => {
    const obj = construireExportModele(inv);
    await exporterFichier({
      nomFichier: nomFichierSafe(inv.nom, 'modele'),
      contenu: JSON.stringify(obj, null, 2),
    });
  });

  const exportComplet = () => faire('complet', t('inventaire.transfExportComplet'), async () => {
    let obj = construireExportComplet(inv, items, { avecPhotos });
    if (avecPhotos) obj = await embarquerPhotosBase64(obj);
    await exporterFichier({
      nomFichier: nomFichierSafe(inv.nom, 'complet'),
      contenu: JSON.stringify(obj),
    });
  });

  const exportPdf = (variante) => faire(`pdf-${variante}`, t('inventaire.transfExportPdf'), async () => {
    await exporterInventairePdf({
      inv, items, variante,
      photos: pdfPhotos, categoriesVides: pdfCatsVides, proprietes: pdfProps,
      nomCalendrier: espaceActif?.nom || '',
      emplacements, avecPlans: pdfPlans,
    });
  });

  const planche = () => faire('qr', t('inventaire.transfPlancheQr'), async () => {
    if (!(emplacements || []).length) throw new Error(t('inventaire.transfAucunEmplacement'));
    await imprimerPlancheQr(inv, emplacements);
  });

  const importer = () => faire('import', t('inventaire.transfImport'), async () => {
    const parsed = await lireFichierInventaire();
    if (!parsed) return;                                  // annulé par l'usager
    const prep = preparerImportComplet(parsed);
    if (parsed.avecPhotos) {
      // Les photos arrivent en base64 : on les remet dans NOTRE Storage.
      await materialiserPhotos(prep, (dataUri) => uploadFichier(dataUri, dossierPhotos));
    }
    await onImporter(prep, parsed);
  });

  const bascule = (on, set, label, aide) => (
    <TouchableOpacity style={s.opt} onPress={() => set(v => !v)}>
      <Text style={s.optCase}>{on ? '☑️' : '⬜'}</Text>
      <View style={{ flex: 1 }}>
        <Text style={s.optTxt}>{label}</Text>
        {!!aide && <Text style={s.optAide}>{aide}</Text>}
      </View>
    </TouchableOpacity>
  );

  const action = (cle, emoji, titre, desc, onPress) => (
    <TouchableOpacity style={s.action} onPress={onPress} disabled={!!busy}>
      <Text style={s.actionEmoji}>{emoji}</Text>
      <View style={{ flex: 1 }}>
        <Text style={s.actionTitre}>{titre}</Text>
        <Text style={s.actionDesc}>{desc}</Text>
      </View>
      {busy === cle ? <ActivityIndicator color={UI.accent} /> : <Text style={s.chev}>›</Text>}
    </TouchableOpacity>
  );

  return (
    <Feuille visible={visible} titre={t('inventaire.transfTitre')} emoji="📤" onFermer={onFermer}>
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}
      {!!msg && <Text style={s.ok}>✓ {msg}</Text>}

      <Text style={s.sect}>{t('inventaire.exporter')}</Text>
      {action('modele', '🧩', t('inventaire.transfModeleTitre'),
        t('inventaire.transfModeleDesc'),
        exportModele)}
      {action('complet', '📦', t('inventaire.transfCompletTitre'),
        t('inventaire.transfCompletDesc'), exportComplet)}
      {bascule(avecPhotos, setAvecPhotos, t('inventaire.transfEmbarquerPhotos'),
        t('inventaire.transfEmbarquerPhotosDesc'))}

      <Text style={s.sect}>{t('inventaire.imprimer')}</Text>
      {action('pdf-recap', '📄', t('inventaire.transfPdfRecapTitre'),
        t('inventaire.transfPdfRecapDesc'), () => exportPdf('recap'))}
      {action('pdf-comptage', '📋', t('inventaire.transfPdfComptageTitre'),
        t('inventaire.transfPdfComptageDesc'), () => exportPdf('comptage'))}
      {bascule(pdfPhotos, setPdfPhotos, t('inventaire.transfAvecPhotos'), null)}
      {bascule(pdfProps, setPdfProps, t('inventaire.transfAvecProprietes'), null)}
      {bascule(pdfCatsVides, setPdfCats, t('inventaire.transfInclureCatsVides'), null)}
      {bascule(pdfPlans, setPdfPlans, t('inventaire.transfInclurePlans'), null)}
      {action('qr', '🏷️', t('inventaire.transfPlancheQrTitre'),
        t('inventaire.transfPlancheQrDesc'), planche)}

      <Text style={s.sect}>{t('inventaire.importerTitre')}</Text>
      {action('import', '📥', t('inventaire.transfImportTitre'),
        t('inventaire.transfImportDesc'), importer)}
      <Text style={s.aide}>
        {t('inventaire.transfImportAide')}
      </Text>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '82%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  corps:   { padding: 16 },
  hIc:     { fontSize: 19, color: UI.accent },
  sect:    { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase',
             letterSpacing: 0.4, marginTop: 18, marginBottom: 9 },
  action:      { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card,
                 borderRadius: 12, borderWidth: 1, borderColor: UI.border, padding: 13, marginBottom: 8 },
  actionEmoji: { fontSize: 22 },
  actionTitre: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  actionDesc:  { fontSize: 11.5, color: UI.muted, marginTop: 2, lineHeight: 16 },
  chev:        { fontSize: 22, color: UI.faint, fontWeight: '300' },
  opt:     { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 7, paddingHorizontal: 3 },
  optCase: { fontSize: 16 },
  optTxt:  { fontSize: 13, color: UI.ink },
  optAide: { fontSize: 11, color: UI.faint, marginTop: 1 },
  aide:    { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginTop: 10 },
  err:     { color: UI.danger, fontSize: 12.5, fontWeight: '600', marginBottom: 8 },
  ok:      { color: UI.accent, fontSize: 12.5, fontWeight: '600', marginBottom: 8 },
});
